import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Copy, Download, LoaderCircle, RotateCw, WrapText, X } from "lucide-react";
import { api } from "./api.ts";
import type { Artifact, ArtifactDetail } from "./types.ts";

import { copyText, type CopyEnvironment, type LegacyCopyDocument } from "./clipboard.ts";

type CopyState = "idle" | "copying" | "copied" | "error";

export { copyText, type CopyEnvironment, type LegacyCopyDocument };

export function ArtifactPreviewDialog({ artifact, onClose }: { artifact: Artifact; onClose: () => void }) {
  const titleId = useId();
  const closeButton = useRef<HTMLButtonElement>(null);
  const copyResetTimer = useRef<number | null>(null);
  const [detail, setDetail] = useState<ArtifactDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [wrapped, setWrapped] = useState(true);
  const [copyState, setCopyState] = useState<CopyState>("idle");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDetail(null);
    try {
      setDetail(await api.get<ArtifactDetail>(`/api/v1/artifacts/${artifact.artifact_id}`));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Artifact 加载失败");
    } finally {
      setLoading(false);
    }
  }, [artifact.artifact_id]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { onClose(); return; }
      if (event.key !== "Tab") return;
      const dialog = closeButton.current?.closest<HTMLElement>(".artifact-dialog");
      const focusable = dialog ? Array.from(dialog.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')).filter((element) => element.offsetParent !== null) : [];
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    closeButton.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      if (copyResetTimer.current !== null) window.clearTimeout(copyResetTimer.current);
      previousFocus?.focus();
    };
  }, [onClose]);

  const formatted = useMemo(() => detail ? formatArtifactData(detail.data) : "", [detail]);
  const copy = async () => {
    if (!detail) return;
    setCopyState("copying");
    const copied = await copyText(formatted);
    setCopyState(copied ? "copied" : "error");
    copyResetTimer.current = window.setTimeout(() => setCopyState("idle"), 1800);
  };

  const modal = (
    <div className="artifact-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="artifact-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="artifact-dialog-header">
          <div>
            <span className="artifact-stage">{artifact.stage}</span>
            <h2 id={titleId}>{artifact.kind}</h2>
            <p>Schema {artifact.schema_version} · {bytes(artifact.byte_size)} · {dateTime(artifact.generated_at)}</p>
          </div>
          <button ref={closeButton} className="icon-button artifact-close" onClick={onClose} title="关闭预览" aria-label="关闭预览"><X /></button>
        </header>

        <div className="artifact-toolbar">
          <div className="artifact-identity"><span>JSON</span><code>{shortHash(artifact.sha256)}</code></div>
          <div className="artifact-tools">
            <button className={`icon-button ${wrapped ? "active" : ""}`} onClick={() => setWrapped((current) => !current)} title={wrapped ? "关闭自动换行" : "开启自动换行"} aria-pressed={wrapped}><WrapText /></button>
            <button className="secondary-button" onClick={() => void copy()} disabled={!detail || copyState === "copying"}>
              {copyState === "copied" ? <Check /> : <Copy />}{copyState === "copied" ? "已复制" : copyState === "error" ? "复制失败" : "复制 JSON"}
            </button>
            <a className="secondary-button" href={`/api/v1/artifacts/${artifact.artifact_id}/download`}><Download />下载</a>
          </div>
        </div>

        <div className={`artifact-content ${wrapped ? "wrapped" : ""}`}>
          {loading && <div className="artifact-loading" role="status"><LoaderCircle className="spin" /><span>正在读取 Artifact</span></div>}
          {!loading && error && <div className="artifact-error" role="alert"><strong>无法打开 Artifact</strong><p>{error}</p><button className="secondary-button" onClick={() => void load()}><RotateCw />重试</button></div>}
          {!loading && detail && <pre tabIndex={0}><code>{formatted}</code></pre>}
        </div>
      </section>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
}

function formatArtifactData(value: unknown): string {
  if (typeof value === "string") return value;
  const formatted = JSON.stringify(value, null, 2);
  return formatted ?? String(value);
}


function shortHash(value: string): string { return value ? value.slice(0, 12) : "-"; }
function bytes(value: number): string { if (value < 1024) return `${value} B`; if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`; return `${(value / 1024 / 1024).toFixed(1)} MB`; }
function dateTime(value: string | null): string { return value ? new Date(value).toLocaleString("zh-CN", { hour12: false }) : "-"; }
